import React, {
  useMemo,
  useState,
  ReactNode,
  createContext,
  useContext,
  useCallback,
} from "react";

import {
  getAdminAccountsSearch,
  getAdminInvitedAccounts,
} from "@/api/admin/account";
import { getAPIErrorDetailAndMessage } from "@/api/helpers";
import { AccountItem, AccountRaw, InvitedAccountRaw } from "@/domain/Account";

type AccountsTab = "accounts" | "invited";
type SearchState<T> = {
  tab: AccountsTab;
  items: T[];
  total: number;
  page: number;
  limit: number;
  email: string;
  isLoaded: boolean;
  isLoading: boolean;
};
type SearchParams = {
  tab: AccountsTab;
  limit?: number;
  page?: number;
  email?: string;
};

const adminAccountsCtx = createContext<{
  searchState: SearchState<AccountItem>;
  runSearchAccounts: (params: SearchParams) => Promise<void>;
  updateAccountInState: (updated: AccountItem) => void;
  accounts: AccountRaw[];
  invitedAccounts: InvitedAccountRaw[];
}>({
  searchState: {
    tab: "accounts",
    items: [],
    total: 0,
    page: 0,
    limit: 100,
    email: "",
    isLoaded: false,
    isLoading: false,
  },
  runSearchAccounts: async () => {
    // nop
  },
  updateAccountInState: () => {
    // nop
  },
  accounts: [],
  invitedAccounts: [],
});

const initialSearchState: SearchState<AccountRaw> = {
  tab: "accounts",
  items: [],
  total: 0,
  page: 0,
  limit: 100,
  email: "",
  isLoaded: false,
  isLoading: false,
};

export const AdminAccountsProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [accounts, setAccounts] = useState<AccountRaw[]>([]);
  const [invitedAccounts, setInvitedAccounts] = useState<InvitedAccountRaw[]>(
    []
  );
  const [searchState, setSearchState] =
    useState<SearchState<AccountItem>>(initialSearchState);
  const [_, setError] = useState<unknown>(null);

  const runSearchAccounts = useCallback(async (params: SearchParams) => {
    const { tab, limit = 100, page = 0, email = "" } = params;

    const updateSearchState = (items: AccountItem[], total: number) => {
      setSearchState({
        tab,
        items,
        total,
        page,
        limit,
        email,
        isLoaded: true,
        isLoading: false,
      });
    };

    setSearchState((prev) => ({
      ...prev,
      tab,
      page,
      limit,
      email,
      isLoading: true,
    }));

    try {
      if (tab === "invited") {
        const response = await getAdminInvitedAccounts({
          limit,
          page,
          email: email || undefined,
        });
        setInvitedAccounts(response.items);
        updateSearchState(response.items, response.total);
        return;
      }

      const response = await getAdminAccountsSearch({
        limit,
        page,
        email: email || undefined,
      });
      setAccounts(response.items);
      updateSearchState(response.items, response.total);
    } catch (err: unknown) {
      setError(() => {
        if (typeof err == "object") {
          const e = {
            ...err,
            message: getAPIErrorDetailAndMessage(err),
          } as Error;
          throw e;
        } else {
          throw new Error("不明なエラー");
        }
      });
      setSearchState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, []);

  const updateAccountInState = useCallback((updated: AccountItem) => {
    if (updated.is_new_user) {
      setInvitedAccounts((prev) =>
        prev.map((a) => (a.idp_uid === updated.idp_uid ? updated : a))
      );
    } else {
      setAccounts((prev) =>
        prev.map((a) => (a.uid === updated.uid ? updated : a))
      );
    }
    setSearchState((prev) => ({
      ...prev,
      items: prev.items.map((a) => {
        if (updated.is_new_user) {
          return a.idp_uid === updated.idp_uid ? updated : a;
        }
        return !a.is_new_user && a.uid === updated.uid ? updated : a;
      }),
    }));
  }, []);

  const ctxValue = useMemo(
    () => ({
      searchState,
      runSearchAccounts,
      updateAccountInState,
      accounts,
      invitedAccounts,
    }),
    [
      searchState,
      runSearchAccounts,
      updateAccountInState,
      accounts,
      invitedAccounts,
    ]
  );

  return (
    <adminAccountsCtx.Provider value={ctxValue}>
      {children}
    </adminAccountsCtx.Provider>
  );
};

export const useAdminAccounts = () => {
  return useContext(adminAccountsCtx);
};
